"use client";

import Link from "next/link";
import { ROUTES } from "@lib/routes";
import { Suspense } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { useTranslations } from "@i18n/provider";
import { getCurriculumBackNav } from "@lib/curriculum/curriculumBackNav";
import { ConvoIcon } from "@ui/ConvoIcon";

const NAV_ITEMS = [
  {
    href: ROUTES.curriculum,
    key: "nav.curriculum" as const,
    icon: "school",
    match: (p: string) =>
      (p.startsWith(ROUTES.curriculum) || p === ROUTES.home) && !p.includes(ROUTES.homework),
  },
  {
    href: ROUTES.homework,
    key: "nav.homework" as const,
    icon: "assignment",
    match: (p: string) => p === ROUTES.homework || p.endsWith(ROUTES.homework),
  },
  {
    href: ROUTES.progress,
    key: "nav.progress" as const,
    icon: "monitoring",
    match: (p: string) => p.startsWith(ROUTES.progress),
  },
  {
    href: ROUTES.settings,
    key: "nav.settings" as const,
    icon: "settings",
    match: (p: string) => p.startsWith(ROUTES.settings),
  },
];

function NavBackLink() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { t } = useTranslations();
  const backNav = getCurriculumBackNav(pathname, searchParams);

  if (!backNav) return null;

  return (
    <Link href={backNav.href} className="nav-back-link">
      {t(backNav.labelKey)}
    </Link>
  );
}

/**
 * The top bar (with its back link and home wordmark) is hidden on phones, so
 * this restores an "up" target as a floating button - mirroring the tutor
 * launcher on the opposite corner. It walks the hierarchy: lesson sub-pages go
 * back to the board, and every other non-home page goes back to the home hub
 * (which has no tab of its own). Phone-only via CSS; desktop keeps the in-bar
 * link and wordmark.
 */
function NavBackFab() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { t } = useTranslations();
  const backNav = getCurriculumBackNav(pathname, searchParams);

  const target: { href: string; labelKey: string } | null =
    backNav ??
    (pathname === ROUTES.home ? null : { href: ROUTES.home, labelKey: "nav.backToHome" });

  if (!target) return null;

  // These labels carry a leading "← " for the desktop text link; strip it so
  // the icon button's accessible name is just the destination.
  const label = t(target.labelKey).replace(/^←\s*/, "");

  return (
    <Link href={target.href} className="nav-back-fab" aria-label={label}>
      <svg
        viewBox="0 0 24 24"
        width="22"
        height="22"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M15 5l-7 7 7 7" />
      </svg>
    </Link>
  );
}

export function AppNav({ isLoggedIn = false }: { isLoggedIn?: boolean }) {
  const pathname = usePathname();
  const { t } = useTranslations();

  // Every app route requires auth and redirects here, so the only logged-out
  // surfaces are the self-branded login pages. They own their own chrome, so
  // suppress the whole nav shell (top bar, tab bar, and floating back button) -
  // otherwise a phone would show tabs that bounce back to login and a back
  // button with nowhere to go.
  if (!isLoggedIn) return null;

  return (
    <>
      <header className="atelier-nav atelier-nav-signed-in">
        <div className="flex min-w-0 items-center gap-3 sm:gap-4">
          <Link href={ROUTES.home} className="flex shrink-0 items-center gap-2.5">
            <ConvoIcon
              className="h-8 w-8 shrink-0"
              style={{ filter: "drop-shadow(0 4px 12px rgba(201, 106, 69, 0.5))" }}
            />
            <span className="font-display text-[1.375rem] font-bold lowercase text-ink">convo</span>
          </Link>
          <Suspense fallback={null}>
            <NavBackLink />
          </Suspense>
        </div>

        <nav className="hidden shrink-0 items-center gap-7 sm:flex" aria-label="Main">
          {NAV_ITEMS.map(({ href, key, match }) => {
            const active = match(pathname);
            return (
              <Link
                key={href}
                href={href}
                className={active ? "nav-link nav-link-active" : "nav-link"}
              >
                {t(key)}
              </Link>
            );
          })}
        </nav>
      </header>

      <nav className="atelier-tabbar" aria-label="Main">
        {NAV_ITEMS.map(({ href, key, icon, match }) => {
          const active = match(pathname);
          return (
            <Link
              key={href}
              href={href}
              className={active ? "tab-link tab-link-active" : "tab-link"}
            >
              <span className="material-symbols-outlined tab-link-icon" aria-hidden="true">
                {icon}
              </span>
              <span className="tab-link-label">{t(key)}</span>
            </Link>
          );
        })}
      </nav>

      <Suspense fallback={null}>
        <NavBackFab />
      </Suspense>
    </>
  );
}
